import PropTypes from "prop-types";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import {
  buildFilterLabels,
  buildGroupsKey,
  countVisible,
  filterSortAndGroup,
} from "../utils.js";

const ShopFiltersContext = createContext(null);

export function ShopFiltersProvider({
  allProducts,
  formatOptions,
  signingTypes,
  characterList,
  vaList,
  charactersMap,
  children,
}) {
  const [activeFormat, setActiveFormat] = useState("all");
  const [activeSigningType, setActiveSigningType] = useState("all");
  const [activeCharacter, setActiveCharacter] = useState(null);
  const [activeVa, setActiveVa] = useState(null);
  const [sortBy, setSortBy] = useState("featured");
  const [searchQuery, setSearchQuery] = useState("");

  const filters = useMemo(
    () => ({
      activeFormat,
      activeSigningType,
      activeCharacter,
      activeVa,
      sortBy,
      searchQuery,
    }),
    [
      activeFormat,
      activeSigningType,
      activeCharacter,
      activeVa,
      sortBy,
      searchQuery,
    ],
  );

  const hasActiveFilters = !!(
    activeFormat !== "all" ||
    activeSigningType !== "all" ||
    activeCharacter ||
    activeVa ||
    searchQuery.trim()
  );

  const handleClearAll = useCallback(() => {
    setActiveFormat("all");
    setActiveSigningType("all");
    setActiveCharacter(null);
    setActiveVa(null);
    setSortBy("featured");
    setSearchQuery("");
  }, []);

  const { groups: filteredGroups, featuredGroup } = useMemo(
    () => filterSortAndGroup(allProducts, filters, charactersMap),
    [allProducts, filters, charactersMap],
  );

  const totalVisible = useMemo(
    () => countVisible(filteredGroups),
    [filteredGroups],
  );

  const groupsKey = useMemo(() => buildGroupsKey(filters), [filters]);

  const filterLabels = useMemo(
    () => buildFilterLabels(filters, formatOptions, signingTypes),
    [filters, formatOptions, signingTypes],
  );

  const value = useMemo(
    () => ({
      activeFormat,
      activeSigningType,
      activeCharacter,
      activeVa,
      sortBy,
      searchQuery,
      setActiveFormat,
      setActiveSigningType,
      setActiveCharacter,
      setActiveVa,
      setSortBy,
      setSearchQuery,
      hasActiveFilters,
      handleClearAll,
      filteredGroups,
      featuredGroup,
      totalVisible,
      groupsKey,
      filterLabels,
      characters: characterList,
      formatOptions,
      signingTypes,
      vaList,
    }),
    [
      activeFormat,
      activeSigningType,
      activeCharacter,
      activeVa,
      sortBy,
      searchQuery,
      hasActiveFilters,
      handleClearAll,
      filteredGroups,
      featuredGroup,
      totalVisible,
      groupsKey,
      filterLabels,
      characterList,
      formatOptions,
      signingTypes,
      vaList,
    ],
  );

  return (
    <ShopFiltersContext.Provider value={value}>
      {children}
    </ShopFiltersContext.Provider>
  );
}

ShopFiltersProvider.propTypes = {
  allProducts: PropTypes.array.isRequired,
  formatOptions: PropTypes.array.isRequired,
  signingTypes: PropTypes.array.isRequired,
  characterList: PropTypes.array,
  vaList: PropTypes.array,
  charactersMap: PropTypes.object,
  children: PropTypes.node.isRequired,
};

export function useShopFilters() {
  const ctx = useContext(ShopFiltersContext);
  if (!ctx) {
    throw new Error("useShopFilters must be used within a ShopFiltersProvider");
  }
  return ctx;
}
